import React, { useState, useContext, useEffect } from "react";
import { Chart, Line } from "react-chartjs-2";
import "chartjs-plugin-annotation";
import moment from "moment";
import "moment-timezone";
import { ChartDataContext } from "../../context/ChartDataContext";

/* custom styles */
import "./Timeseries.scss";

// This is a silly global instead of a state/context because making it a state is causing a big performance hit
// with the tooltip cursor hover performance.
// TODO : figure out how to do this right.
let hoveredTimeString = "";
// Time threshold between cursor and point to display in tool tip
const TT_TH_MINS = 2 * 60;

const Timeseries = ({
  startDate,
  endDate,
  observationData,
  optimalPointwiseData,
  optimalPointwiseTimestampData,
  selectedChart,
  selectedGraph,
  timestampData,
  tooltipName
}) => {
  const graphUnits = {
    wave: {
      unit: "m"
    },
    period: {
      unit: "s",
      stepSize: 4
    },
    spread: {
      unit: "°",
      max: 90,
      stepSize: 30
    },
    direction: {
      unit: "°",
      max: 360,
      stepSize: 90
    }
  };

  const {
    trailTimestamp,
    selectedDataPoint,
    setSelectedDataPoint,
    setTrailTimestamp
  } = useContext(ChartDataContext);

  const timestampData2 = timestampData ? timestampData : [];
  let fullObservationData = [[], []];
  let observationNullspace = [];
  if (observationData) {
    fullObservationData =
      selectedChart.length > 1
        ? [
            observationData.map(record =>
              record[selectedChart[1]] === undefined
                ? null
                : record[selectedChart[1]].toFixed(1)
            ),
            observationData.map(record =>
              record[selectedChart[0]] === undefined
                ? null
                : record[selectedChart[0]].toFixed(1)
            )
          ]
        : [
            observationData.map(record =>
              record[selectedChart[0]] === undefined
                ? null
                : record[selectedChart[0]].toFixed(1)
            )
          ];
    observationNullspace = observationData.map(record => null);
  }
  const hcOptimalPointwiseTimestampData = optimalPointwiseTimestampData.filter(
    ele => {
      return moment(ele).isBefore(moment().subtract(6, "hours"));
    }
  );
  const fcOptimalPointwiseTimestampData = optimalPointwiseTimestampData.filter(
    ele => {
      return moment(ele).isAfter(moment().subtract(6, "hours"));
    }
  );
  const hcOptimalPointwiseData = optimalPointwiseData.slice(
    0,
    hcOptimalPointwiseTimestampData.length
  );
  const fcOptimalPointwiseData = optimalPointwiseData.slice(
    hcOptimalPointwiseTimestampData.length
  );
  const hcOptimalPointwiseNullspace = hcOptimalPointwiseData.map(
    record => null
  );
  const fcOptimalPointwiseNullspace = fcOptimalPointwiseData.map(
    record => null
  );
  let largest = observationData
    ? Math.ceil(
        Math.max.apply(0, [...optimalPointwiseData, ...fullObservationData[0]])
      ) + 1
    : Math.ceil(Math.max.apply(0, [...optimalPointwiseData])) + 1;
  const newLargest = largest % 2 === 0 ? largest : largest + 1;
  const fullTimestamp = [
    ...hcOptimalPointwiseTimestampData,
    ...fcOptimalPointwiseTimestampData,
    ...timestampData2,
    moment(startDate).startOf("day"),
    moment(endDate).startOf("day")
  ];

  let chartRef = {};

  let observationalData =
    selectedChart.length === 2
      ? [
          {
            backgroundColor: "none",
            borderColor: "#EA6B72",
            borderWidth: 2,
            data: [
              ...hcOptimalPointwiseData,
              ...fcOptimalPointwiseNullspace,
              ...observationNullspace,
              null,
              null
            ],
            fill: false,
            label: `Sofar Model I - ${tooltipName[1]}`,
            lineTension: 0,
            pointBackgroundColor: "rgba(255,255,255,0)",
            pointBorderColor: "rgba(255,255,255,0)",
            pointHitRadius: 20,
            radius: 0,
            spanGaps: false
          },
          {
            backgroundColor: "none",
            borderColor: "#EA6B72",
            borderDash: [4, 5],
            borderWidth: 2,
            data: [
              ...hcOptimalPointwiseNullspace,
              ...fcOptimalPointwiseData,
              ...observationNullspace,
              null,
              null
            ],
            fill: false,
            label: `Sofar Model II - ${tooltipName[1]}`,
            lineTension: 0,
            pointBackgroundColor: "rgba(255,255,255,0)",
            pointBorderColor: "rgba(255,255,255,0)",
            pointHitRadius: 20,
            radius: 0,
            spanGaps: false
          },
          {
            backgroundColor: "none",
            borderColor: "#E3C734",
            borderWidth: 2,
            data: [
              ...hcOptimalPointwiseNullspace,
              ...fcOptimalPointwiseNullspace,
              ...fullObservationData[1],
              null,
              null
            ],
            fill: false,
            label: `Observation - ${tooltipName[0]}`,
            lineTension: 0,
            pointBackgroundColor: "rgba(255,255,255,0)",
            pointBorderColor: "rgba(255,255,255,0)",
            pointHitRadius: 20,
            radius: 0,
            spanGaps: false
          },
          {
            backgroundColor: "none",
            borderColor: "rgba(183,76,164,1)",
            borderWidth: 2,
            data: [
              ...hcOptimalPointwiseNullspace,
              ...fcOptimalPointwiseNullspace,
              ...fullObservationData[0],
              null,
              null
            ],
            fill: false,
            label: `Observation - ${tooltipName[1]}`,
            lineTension: 0,
            pointBackgroundColor: "rgba(255,255,255,0)",
            pointBorderColor: "rgba(255,255,255,0)",
            pointHitRadius: 20,
            radius: 0,
            spanGaps: false
          }
        ]
      : [
          {
            backgroundColor: "none",
            borderColor: "#EA6B72",
            borderWidth: 2,
            data: [
              ...hcOptimalPointwiseData,
              ...fcOptimalPointwiseNullspace,
              ...observationNullspace,
              null,
              null
            ],
            fill: false,
            label: "Wave Height Model - I",
            lineTension: 0,
            radius: 0,
            spanGaps: false,
            pointBackgroundColor: "rgba(255,255,255,0)",
            pointBorderColor: "rgba(255,255,255,0)",
            pointHitRadius: () => {
              // TODO : scale pointHitRadius so it will grab +/- TT_TH_MINS
              return 20;
            }
          },
          {
            backgroundColor: "none",
            borderColor: "#EA6B72",
            borderDash: [3, 5],
            borderWidth: 2,
            data: [
              ...hcOptimalPointwiseNullspace,
              ...fcOptimalPointwiseData,
              ...observationNullspace,
              null,
              null
            ],
            fill: false,
            label: "Wave Height Model - II",
            lineTension: 0,
            radius: 0,
            spanGaps: false,
            pointBackgroundColor: "rgba(255,255,255,0)",
            pointBorderColor: "rgba(255,255,255,0)",
            pointHitRadius: () => {
              // TODO : scale pointHitRadius so it will grab +/- TT_TH_MINS
              return 20;
            }
          },
          {
            backgroundColor: "none",
            borderColor: "#E3C734",
            borderWidth: 2,
            data: [
              ...hcOptimalPointwiseTimestampData,
              ...fcOptimalPointwiseNullspace,
              ...fullObservationData[0],
              null,
              null
            ],
            fill: false,
            label: "Wave Height Observations",
            lineTension: 0,
            radius: 0,
            spanGaps: false,
            pointBackgroundColor: "rgba(255,255,255,0)",
            pointBorderColor: "rgba(255,255,255,0)",
            pointHitRadius: () => {
              // TODO : scale pointHitRadius so it will grab +/- TT_TH_MINS
              return 20;
            }
          }
        ];

  let stepSize =
    selectedChart.length > 1
      ? {
          min: 0,
          max: graphUnits[selectedGraph].max,
          stepSize: graphUnits[selectedGraph].stepSize
        }
      : {
          min: 0,
          max: newLargest,
          stepSize: newLargest <= 4 ? 1 : 2
        };

  useEffect(() => {
    /* vertical line */
    Chart.pluginService.register({
      afterDraw: chart => {
        if (chart.tooltip._active && chart.tooltip._active.length) {
          const activePoint = chart.controller.tooltip._active[0];
          const ctx = chart.ctx;
          const x = activePoint._chart.tooltip._eventPosition.x;
          const topY = chart.scales["y-axis-0"].top;
          const bottomY = chart.scales["y-axis-0"].bottom;
          ctx.save();
          ctx.beginPath();
          ctx.moveTo(x, topY);
          ctx.lineTo(x, bottomY);
          ctx.lineWidth = 2;
          ctx.strokeStyle = "rgba(255, 255, 255, 0.15)";
          ctx.stroke();
          ctx.restore();
        }
      },
      beforeDraw: function(chart, easing) {
        if (
          chart.config.options.chartArea &&
          chart.config.options.chartArea.backgroundColor
        ) {
          var ctx = chart.chart.ctx;
          var chartArea = chart.chartArea;

          ctx.save();
          ctx.fillStyle = chart.config.options.chartArea.backgroundColor;
          ctx.fillRect(
            (chartArea.right - chartArea.left + 50) / 2,
            chartArea.top,
            chartArea.right - chartArea.left,
            chartArea.bottom - chartArea.top
          );
          ctx.restore();
        }
      }
    });

    Chart.Tooltip.positioners.custom = function(elements, position) {
      if (!elements.length) {
        return false;
      }

      let offset;

      //adjust the offset left or right depending on the event position
      if (elements[0]._chart.width / 20 > position.x) {
        offset = -20;
      } else if (elements[0]._chart.width - position.x > 50) {
        offset = -70;
      } else {
        offset = -120;
      }

      return {
        x: position.x + offset,
        y: position.y
      };
    };
  }, []);

  const data = {
    labels: fullTimestamp,
    datasets: observationalData,
    yaxis: {
      labels: {
        style: {
          color: "#FFF",
          fontSize: "10px"
        }
      }
    }
  };

  return (
    <div className="timeseries-graph">
      <Line
        ref={reference => (chartRef = reference)}
        data={data}
        options={{
          animation: false,
          annotation: {
            drawTime: "afterDatasetsDraw",
            annotations: [
              {
                type: "line",
                mode: "vertical",
                scaleID: "x-axis-0",
                value: trailTimestamp,
                borderColor: `${
                  selectedDataPoint
                    ? "rgba(255, 255, 255, 0.15)"
                    : "rgba(0, 0, 0, 0)"
                }`,
                label: {
                  backgroundColor: "rgba(0,0,0,0)",
                  content: `${moment(trailTimestamp).format(
                    "MMM DD, YYYY - LT"
                  )} (${moment
                    .tz(
                      trailTimestamp,
                      Intl.DateTimeFormat().resolvedOptions().timeZone
                    )
                    .format("z")})`,
                  enabled: selectedDataPoint,
                  fontColor: "rgba(255, 255, 255, 0.5)",
                  position: "top"
                }
              }
            ]
          },
          chartArea: {
            backgroundColor: "rgba(21, 28, 49, 0.75)"
          },
          legend: {
            display: false,
            position: "left",
            labels: {
              fontColor: "#FFF",
              padding: 15,
              usePointStyle: true
            }
          },
          tooltips: {
            mode: "x",
            intersect: false,
            enabled: false,
            position: "custom",
            xPadding: 10,
            yPadding: 10,
            callbacks: {
              title: () =>
                `${moment(hoveredTimeString).format("LT, MMM DD")} ${moment
                  .tz(
                    hoveredTimeString,
                    Intl.DateTimeFormat().resolvedOptions().timeZone
                  )
                  .format("z")}`,
              label: (tooltipItem, data) =>
                ` ${data.datasets[tooltipItem.datasetIndex].label}: ${
                  tooltipItem.value
                }${graphUnits[selectedGraph].unit}`,
              labelColor: function(tooltipItem, data) {
                var dataset =
                  data.config.data.datasets[tooltipItem.datasetIndex];
                return {
                  backgroundColor: dataset.borderColor
                };
              }
            },
            custom: tooltipModel => {
              // Tooltip Element
              var tooltipEl = document.getElementById("chartjs-tooltip");

              // Create element on first render
              if (!tooltipEl) {
                tooltipEl = document.createElement("div");
                tooltipEl.id = "chartjs-tooltip";
                tooltipEl.innerHTML = "<table></table>";
                document.body.appendChild(tooltipEl);
              }

              // Hide if no tooltip
              if (tooltipModel.opacity === 0) {
                tooltipEl.style.opacity = 0;
                return;
              }

              // Set caret Position
              tooltipEl.classList.remove("above", "below", "no-transform");
              if (tooltipModel.yAlign) {
                tooltipEl.classList.add(tooltipModel.yAlign);
              } else {
                tooltipEl.classList.add("no-transform");
              }

              function getBody(bodyItem) {
                return bodyItem.lines;
              }

              // Set Text
              if (tooltipModel.body) {
                var titleLines = tooltipModel.title || [];
                const waveId = document.querySelector("[id*='wave']");
                const peakId = document.querySelector("[id*='peak']");
                const meanId = document.querySelector("[id*='mean']");
                const modelId = document.querySelector("[id*='model']");
                var bodyLines = tooltipModel.body.map(getBody);
                var dataPoints = tooltipModel.dataPoints;
                const datasetIndexValues = [];
                const pointsIndices = [];

                var innerHtml = "<thead>";

                titleLines.forEach(function(title) {
                  innerHtml +=
                    '<tr><th style="font-size: 1.3em">' + title + "</th></tr>";
                });
                innerHtml += "</thead><tbody>";
                // NOTE : this assumes and requires that dataPoints are ordered by datasetIndex
                // then by label (time). Additionally labels and timestring must be in
                // lexicographically ordered format (eg ISO8061).
                let workingDSIndex = null;
                let minCandItem = null;
                let minCandIndex = -1;
                let maxCandItem = null;
                let maxCandIndex = -1;

                const processCandidates = () => {
                  let minCandDiff,
                    maxCandDiff = Infinity;
                  let selectedIndex = null;
                  if (workingDSIndex === null) return;
                  if (minCandItem)
                    minCandDiff = Math.abs(
                      moment(minCandItem.label).diff(
                        moment(hoveredTimeString),
                        "minutes"
                      )
                    );
                  if (maxCandItem)
                    maxCandDiff = Math.abs(
                      moment(maxCandItem.label).diff(
                        moment(hoveredTimeString),
                        "minutes"
                      )
                    );
                  if (minCandDiff <= TT_TH_MINS && minCandDiff < maxCandDiff)
                    selectedIndex = minCandIndex;
                  else if (maxCandDiff <= TT_TH_MINS)
                    selectedIndex = maxCandIndex;
                  if (selectedIndex) {
                    pointsIndices.push(selectedIndex);
                    datasetIndexValues.push(workingDSIndex);
                  }
                  minCandItem = null;
                  minCandIndex = -1;
                  maxCandItem = null;
                  maxCandIndex = -1;
                };

                dataPoints.forEach((item, index) => {
                  if (item.datasetIndex !== workingDSIndex) {
                    processCandidates();
                    workingDSIndex = item.datasetIndex;
                  }
                  if (item.label < hoveredTimeString) {
                    minCandItem = item;
                    minCandIndex = index;
                  } else if (
                    item.label >= hoveredTimeString &&
                    maxCandItem === null
                  ) {
                    maxCandItem = item;
                    maxCandIndex = index;
                  }
                });
                processCandidates();

                pointsIndices.forEach(value => {
                  const firstDigitInString = bodyLines[value][0].search(/\d/);
                  if (
                    bodyLines[value][0].toLowerCase().indexOf("model") !== -1
                  ) {
                    modelId.innerHTML = bodyLines[value][0].substring(
                      firstDigitInString
                    );
                  } else if (
                    bodyLines[value][0].toLowerCase().indexOf("peak") !== -1
                  ) {
                    peakId.innerHTML = bodyLines[value][0].substring(
                      firstDigitInString
                    );
                  } else if (
                    bodyLines[value][0].toLowerCase().indexOf("mean") !== -1
                  ) {
                    meanId.innerHTML = bodyLines[value][0].substring(
                      firstDigitInString
                    );
                  } else if (
                    bodyLines[value][0].toLowerCase().indexOf("wave") !== -1
                  ) {
                    waveId.innerHTML = bodyLines[value][0].substring(
                      firstDigitInString
                    );
                  }
                });
                innerHtml += "</tbody>";

                var tableRoot = tooltipEl.querySelector("table");
                tableRoot.innerHTML = innerHtml;
              }

              // `this` will be the overall tooltip
              var position = chartRef.chartInstance.canvas.getBoundingClientRect();

              // Display, position, and set styles for font
              tooltipEl.style.opacity = 1;
              tooltipEl.style.background = "transparent";
              tooltipEl.style.borderRadius = "2px";
              tooltipEl.style.color = "rgba(255,255,255,0.5)";
              tooltipEl.style.position = "absolute";
              tooltipEl.style.left =
                position.left + window.pageXOffset + tooltipModel.caretX + "px";
              tooltipEl.style.bottom = "15.5vh";
              tooltipEl.style.fontFamily = tooltipModel._bodyFontFamily;
              tooltipEl.style.fontSize = "9.5px";
              tooltipEl.style.fontStyle = tooltipModel._bodyFontStyle;
              tooltipEl.style.paddingRight = tooltipModel.xPadding + "px";
              tooltipEl.style.paddingBottom = tooltipModel.yPadding + "px ";
              tooltipEl.style.pointerEvents = "none";
              tooltipEl.style.zIndex = 9999;
            }
          },
          maintainAspectRatio: false,
          scales: {
            xAxes: [
              {
                gridLines: {
                  display: false
                },
                ticks: {
                  fontColor: "#FFF"
                },
                type: "time",
                time: {
                  unit: "day"
                }
              }
            ],
            yAxes: [
              {
                gridLines: {
                  borderDash: [2, 30],
                  borderDashOffset: 2,
                  color: "rgba(159, 168, 178, 0.6)",
                  drawBorder: false,
                  offsetGridLines: true
                },
                ticks: {
                  beginAtZero: true,
                  fontColor: "#FFF",
                  fontSize: 10,
                  ...stepSize
                }
              }
            ]
          },
          onClick: (event, i) => {
            const chartInstance = chartRef.chartInstance;
            const capturedTimeIndex = chartInstance.getElementsAtXAxis(event)[0]
              ._index;
            const capturedTimeString = fullTimestamp[capturedTimeIndex];
            console.log(`Selected time: ${capturedTimeString}`);
            setSelectedDataPoint(true);
            setTrailTimestamp(capturedTimeString);
          },
          onHover: event => {
            const chartInstance = chartRef.chartInstance;
            const capturedTimeIndex = chartInstance.getElementsAtXAxis(event)[0]
              ._index;
            hoveredTimeString = fullTimestamp[capturedTimeIndex];
          }
        }}
      />
    </div>
  );
};

export default Timeseries;
